import { ApiError, getApiErrorMessage } from "@/lib/api/error";

import { RecorderError } from "../hooks/use-browser-recorder";
import { COPY } from "./copy";
import { UploadError } from "./multipart-uploader";

/** `reset_at` from a conversations 429 body (`{code, reset_at, retry_after_s}`), or null. */
export function resetAtFromError(error: unknown): string | null {
  if (!(error instanceof ApiError)) return null;
  const v = error.detail.reset_at ?? (typeof error.detail.retry_after_s === "number" ? new Date(Date.now() + error.detail.retry_after_s * 1000).toISOString() : undefined);
  return typeof v === "string" ? v : null;
}

function when(iso: string | null): string {
  return iso ? new Date(iso).toLocaleString() : COPY.errors.later;
}

export function conversationErrorMessage(error: unknown): string {
  if (error instanceof UploadError) {
    switch (error.code) {
      case "UNSUPPORTED_TYPE": return COPY.errors.uploadUnsupportedType;
      case "DURATION_UNREADABLE": return COPY.errors.uploadDurationUnreadable;
      case "UPLOAD_TOO_LARGE": return COPY.errors.uploadTooLarge;      // same copy the server codes use
      case "UPLOAD_TOO_SHORT": return COPY.errors.uploadTooShort;
      case "UPLOAD_TOO_LONG": return COPY.errors.uploadTooLong;
      case "ETAG_MISSING": return COPY.errors.uploadEtagMissing;
      case "PART_FAILED":
      case "NETWORK": return COPY.errors.uploadPartFailed;
    }
  }
  if (error instanceof RecorderError) {
    switch (error.code) {
      case "UNSUPPORTED": return COPY.errors.recorderUnsupported;
      case "TAB_UNSUPPORTED": return COPY.errors.recorderTabUnsupported;
      case "PERMISSION_DENIED": return COPY.errors.recorderPermissionDenied;
      case "NO_DEVICE": return COPY.errors.recorderNoDevice;
      case "DEVICE_BUSY": return COPY.errors.recorderDeviceBusy;
      case "SHARE_CANCELLED": return COPY.errors.recorderShareCancelled;
      case "NO_TAB_AUDIO": return COPY.errors.recorderNoTabAudio;
      case "FAILED": return COPY.errors.recorderFailed;
    }
  }
  if (error instanceof DOMException && error.name === "AbortError") return COPY.errors.uploadCancelled;
  if (error instanceof ApiError) {
    switch (error.code) {
      case "CLASS_LOWERING_REQUIRES_ADMIN": return COPY.errors.classLoweringAdmin;
      case "CLASS_LOWERING_BLOCKED_BY_CONSENT": return COPY.errors.classLoweringConsent;
      case "INVALID_MEETING_TYPE": return COPY.errors.invalidMeetingType;
      case "RELINK_TARGET_FORBIDDEN": {
        const side = typeof error.detail.side === "string" ? error.detail.side : "target";
        return COPY.errors.relinkTargetForbidden(side);
      }
      case "RELINK_PROPOSAL_NOT_AVAILABLE": return COPY.header.relinkAfterReady;
      case "JOB_NOT_FOUND": return COPY.errors.jobNotFound;
      case "GRANT_ENTITY_FORBIDDEN": return COPY.share.entityRefused;
      case "USER_NOT_FOUND": return COPY.errors.userNotFound;
      case "CONVERSATION_UNRESOLVED": return COPY.errors.conversationUnresolved;
      case "MATCH_ALREADY_RESOLVED": return COPY.errors.matchAlreadyResolved;
      case "PROPERTY_TYPE_MISMATCH": return COPY.errors.propertyTypeMismatch;
      case "PROPERTY_NOT_FOUND": return COPY.errors.propertyNotFound;
      case "RETENTION_PREVIEW_REQUIRED": return COPY.errors.retentionPreviewRequired;
      case "RETENTION_PREVIEW_STALE": return COPY.errors.retentionPreviewStale;
      case "RATE_LIMITED": return COPY.errors.rateLimited(when(resetAtFromError(error)));
      case "UPLOAD_PRESIGN_DAILY": return COPY.errors.uploadPresignDaily(when(resetAtFromError(error)));
      case "UPLOAD_BYTES_CAP": return COPY.errors.uploadBytesCap(when(resetAtFromError(error)));
      case "ASR_MINUTES_CAP": return COPY.errors.asrMinutesCap(when(resetAtFromError(error)));
      case "PURGE_RATE_LIMITED": return COPY.errors.purgeRateLimited(when(resetAtFromError(error)));
      case "UPLOAD_TOKEN_INVALID": return COPY.errors.uploadTokenInvalid;
      case "CHANNEL_DISABLED": return COPY.errors.channelDisabled;
      case "FEATURE_NOT_IN_PLAN": return COPY.errors.featureNotInPlan;
      case "SOURCE_NOT_FOUND": return COPY.errors.sourceNotFound;
      case "SPEAKER_LABEL_NOT_FOUND": return COPY.errors.speakerLabelNotFound;
      case "PARTICIPANT_NOT_FOUND": return COPY.errors.participantNotFound;
      case "DOCUMENT_NOT_FOUND": return COPY.errors.documentNotFound;
      case "PROJECT_NOT_FOUND": return COPY.errors.projectNotFound;
      case "PROJECT_ACCESS_DENIED": return COPY.errors.projectAccessDenied;
      case "CANDIDATE_NOT_IN_JOB": return COPY.errors.candidateNotInJob;
      case "CANDIDATE_DECLINED": return COPY.errors.candidateDeclined;
      case "LEGAL_HOLD_ACTIVE": {
        const until = error.detail.legal_hold_until;
        return COPY.errors.legalHoldActive(
          typeof until === "string" ? new Date(until).toLocaleDateString() : COPY.errors.later,
        );
      }
      case "TRANSCRIPT_CAP_REACHED": return COPY.errors.transcriptCapReached;
      case "PARTICIPANT_ALREADY_CLAIMED": return COPY.errors.participantAlreadyClaimed;
      case "CANNOT_SELF_CANDIDATE": return COPY.errors.cannotSelfCandidate;
      case "CONVERSATION_PROCESSING": return COPY.errors.conversationProcessing;
      case "CONVERSATION_PURGED": return COPY.errors.conversationPurged;
      case "SOURCE_NOT_DETACHABLE": return COPY.errors.sourceNotDetachable;
      case "UPLOAD_ALREADY_FINALIZED": return COPY.errors.uploadAlreadyFinalized;
      case "UPLOAD_TOO_LARGE": return COPY.errors.uploadTooLarge;
      case "UPLOAD_SIZE_MISMATCH": return COPY.errors.uploadSizeMismatch;
      case "IMPORT_TOO_LARGE": return COPY.errors.importTooLarge;
      case "UPLOAD_CONTENT_TYPE": return COPY.errors.uploadContentType;
      case "UPLOAD_TOO_SHORT": return COPY.errors.uploadTooShort;
      case "UPLOAD_TOO_LONG": return COPY.errors.uploadTooLong;
      case "IMPORT_SPEAKER_UNKNOWN": return COPY.errors.importSpeakerUnknown;
      case "ATTESTATION_REQUIRED": return COPY.errors.attestationRequired;
      case "CLAIM_ROLE_CONFLICT": return COPY.errors.claimRoleConflict;
      case "INVALID_LANGUAGE": return COPY.errors.invalidLanguage;
      case "PROCESSING_UNAVAILABLE": return COPY.errors.processingUnavailable;
      case "MEDIA_NOT_AVAILABLE": return COPY.errors.mediaNotAvailable;
      // ── spec 04 §5.5 ──
      case "ADHOC_POOL_BUSY": {
        const s = error.detail.retry_after_s;
        return COPY.errors.adhocPoolBusy(typeof s === "number" ? s : 30);
      }
      case "ORG_BOT_LIMIT": {
        const n = error.detail.max_concurrent_bots;
        return COPY.errors.orgBotLimit(typeof n === "number" ? n : 10);
      }
      case "MEETING_URL_INVALID": return COPY.errors.meetingUrlInvalid;
      case "PLATFORM_UNSUPPORTED": return COPY.errors.platformUnsupported;
      case "CAPTURE_DISABLED_FOR_USER": return COPY.errors.captureDisabledForUser;
      case "CAPTURE_NOT_LIVE": return COPY.errors.captureNotLive;
      case "MEETING_ALREADY_CAPTURED": return COPY.errors.meetingAlreadyCaptured;
      case "TRIAGE_NOT_MULTI_CANDIDATE": return COPY.errors.triageNotMultiCandidate;
      case "CALENDAR_NOT_OWNED": return COPY.errors.calendarNotOwned;
      case "CALENDAR_CONNECTION_NOT_FOUND": return COPY.errors.calendarConnectionNotFound;
      case "CALENDAR_EVENT_NOT_FOUND": return COPY.errors.calendarEventNotFound;
      case "CALENDAR_ALREADY_CONNECTED": return COPY.errors.calendarAlreadyConnected;
      case "CALENDAR_DELETED": return COPY.errors.calendarDeleted;
      case "OAUTH_STATE_INVALID": return COPY.errors.oauthStateInvalid;
      case "OAUTH_PROVIDER_ERROR": return COPY.errors.oauthProviderError;
      case "MAX_CALENDARS_REACHED": {
        const n = error.detail.max_calendars;
        return COPY.errors.maxCalendarsReached(typeof n === "number" ? n : 50);
      }
      case "SCHEDULE_OVERRIDE_FORBIDDEN": return COPY.errors.scheduleOverrideForbidden;
      case "SCHEDULE_OVERRIDE_TOO_LATE": return COPY.errors.scheduleOverrideTooLate;
      case "RECALL_UNAVAILABLE": return COPY.errors.recallUnavailable;
      case "CONVERSATION_EDIT_FORBIDDEN": return COPY.header.noEdit;   // 403 from 04b capture_command and 04c /triage — the header already has the copy
      // ── spec 05 §6.8 ──
      case "CONSENT_OVERRIDE_FORBIDDEN": return COPY.errors.consentOverrideForbidden;
      case "CONSENT_STANDING_DECLINE": return COPY.errors.consentStandingDecline;
      case "CONSENT_TOKEN_INVALID": return COPY.errors.consentTokenInvalid;
      case "CONSENT_TOKEN_EXPIRED": return COPY.errors.consentTokenExpired;
      case "CONSENT_TOKEN_USED": return COPY.errors.consentTokenUsed;
      case "CONSENT_CHOICE_MISMATCH": return COPY.errors.consentChoiceMismatch;
      case "CUT_DEFERRED": return COPY.errors.cutDeferred;
      case "CUT_IN_PROGRESS": return COPY.errors.cutInProgress;
      case "REPLY_NOT_FOUND": return COPY.errors.replyNotFound;
      case "REPLY_ALREADY_HANDLED": return COPY.errors.replyAlreadyHandled;
      case "REPLY_NOT_AUTHENTICATED": return COPY.errors.replyNotAuthenticated;
      case "PARTICIPANT_HAS_NO_ADDRESS": return COPY.errors.participantHasNoAddress;
      case "PARTICIPANT_REQUIRED": return COPY.errors.participantRequired;
      case "NOTICE_TEMPLATE_INCOMPLETE": {
        const slot = error.detail.slot_code;
        return COPY.errors.noticeTemplateIncomplete(typeof slot === "string" ? slot : "org_paragraph");
      }
      case "NOTICE_RATE_LIMITED": return COPY.errors.noticeRateLimited(when(resetAtFromError(error)));
      case "ERASURE_REQUEST_NOT_FOUND": return COPY.errors.erasureRequestNotFound;
      case "ERASURE_SUBJECT_INVALID": return COPY.errors.erasureSubjectInvalid;
      case "PROFILE_NOT_FOUND": return COPY.errors.profileNotFound;
      case "DSAR_TOO_LARGE": {
        const n = error.detail.conversations_total;   // 05a Task 14's key
        return COPY.errors.dsarTooLarge(typeof n === "number" ? n : null);
      }
      case "LEGAL_HOLD_INVALID": return COPY.errors.legalHoldInvalid;
      // ── spec 06 §3.5 ──
      case "VERIFIED_DOMAIN_NOT_FOUND": return COPY.errors.verifiedDomainNotFound;
      case "VERIFIED_DOMAIN_EXISTS": return COPY.errors.verifiedDomainExists;
      case "VERIFIED_DOMAIN_LIMIT": {
        const n = error.detail.max;
        return COPY.errors.verifiedDomainLimit(typeof n === "number" ? n : null);
      }
      case "VERIFIED_DOMAIN_INVALID": return COPY.errors.verifiedDomainInvalid;
      case "VERIFIED_DOMAIN_PUBLIC": return COPY.errors.verifiedDomainPublic;
      // ── call summary PDF export ──
      case "SUMMARY_NOT_READY": return COPY.errors.summaryNotReady;
      case "SUMMARY_PDF_UNAVAILABLE": return COPY.errors.summaryPdfUnavailable;
      case "SUMMARY_PDF_FAILED": return COPY.errors.summaryPdfFailed;
      default:
        break;
    }
  }
  return getApiErrorMessage(error);
}
